import React from "react";
import { GoalProgress } from "../../types/kpiGoals";
import { formatNumber } from "../../helpers/formatNumber";

interface GoalProgressCardProps {
  progress: GoalProgress;
}

const RING_SIZE = 120;
const STROKE_WIDTH = 10;
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const GoalProgressCard: React.FC<GoalProgressCardProps> = ({ progress }) => {
  const { goal, percentage, formattedCurrent, formattedTarget } = progress;
  const clampedPercentage = Math.min(percentage, 100);
  const offset = CIRCUMFERENCE - (clampedPercentage / 100) * CIRCUMFERENCE;
  const isComplete = percentage >= 100;

  return (
    <div
      className={`bg-white rounded-lg shadow p-6 flex flex-col items-center ${
        isComplete ? "ring-2 ring-green-400 bg-green-50" : ""
      }`}
    >
      <h4 className="text-sm font-medium text-gray-500 mb-1">{goal.label}</h4>
      <p className="text-xs text-gray-400 mb-4">{goal.description}</p>

      <div className="relative mb-4">
        <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={STROKE_WIDTH}
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={isComplete ? "rgb(34, 197, 94)" : goal.color}
            strokeWidth={STROKE_WIDTH}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-gray-800">
            {formatNumber(percentage, 1)}%
          </span>
        </div>
      </div>

      <div className="text-center">
        <div className="text-lg font-semibold text-gray-800">
          {formattedCurrent}
        </div>
        <div className="text-sm text-gray-400">of {formattedTarget}</div>
      </div>
    </div>
  );
};

export default GoalProgressCard;
